import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyApplications } from '../lib/api/applications';
import type { ApplicationWithDetails } from '../types/database';
import { captureError } from '../lib/capture-error';
import { formatLocalDateRange } from '../lib/dates';

const MyApplicationsPage: React.FC = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState<ApplicationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadApplications = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const data = await getMyApplications(user.id);
        setApplications(data);
      } catch (err) {
        captureError(err, 'Error loading applications');
        setError('Failed to load your applications');
      } finally {
        setLoading(false);
      }
    };

    loadApplications();
  }, [user]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-success';
      case 'rejected':
        return 'bg-danger';
      case 'submitted':
      case 'under_review':
        return 'bg-info';
      case 'waitlisted':
        return 'bg-warning text-dark';
      case 'draft':
      default:
        return 'bg-secondary';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading your applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>My Applications</h1>
        <Link to="/events" className="btn btn-primary">
          <i className="fas fa-calendar-plus me-2"></i>
          Browse Events
        </Link>
      </div>

      {applications.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
            <h3>No Applications Yet</h3>
            <p className="text-muted">
              You haven't applied to any events yet. Browse our upcoming events to get started!
            </p>
            <Link to="/events" className="btn btn-primary mt-2">
              <i className="fas fa-calendar-alt me-2" aria-hidden></i>
              View Upcoming Events
            </Link>
          </div>
        </div>
      ) : (
        <div className="row">
          {applications.map((app) => {
            const event = app.event;
            return (
              <div key={app.id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <span className={`badge ${getStatusBadgeClass(app.status)}`}>
                      {formatStatus(app.status)}
                    </span>
                    {app.submitted_at && (
                      <small className="text-muted">
                        {new Date(app.submitted_at).toLocaleDateString()}
                      </small>
                    )}
                  </div>
                  <div className="card-body">
                    <h5 className="card-title">{event?.title || 'Unknown Event'}</h5>
                    {event && (
                      <p className="card-text text-muted small">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {[event.location_city, event.location_state, event.location_country]
                          .filter(Boolean)
                          .join(', ')}
                        <br />
                        <i className="fas fa-calendar me-1"></i>
                        {formatLocalDateRange(event.start_date, event.end_date)}
                      </p>
                    )}

                    {app.status === 'draft' && (
                      <div className="alert alert-warning small py-2 mb-2">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        Application not submitted yet
                      </div>
                    )}

                    {app.status === 'approved' && (
                      <div className="alert alert-success small py-2 mb-2">
                        <i className="fas fa-check-circle me-1"></i>
                        Congratulations! Your application has been approved.
                      </div>
                    )}

                    {app.status === 'rejected' && (
                      <div className="alert alert-danger small py-2 mb-2">
                        <i className="fas fa-times-circle me-1"></i>
                        Unfortunately, your application was not approved.
                      </div>
                    )}
                  </div>
                  <div className="card-footer bg-transparent">
                    {event && (
                      <Link
                        to={`/events/${event.slug}`}
                        className="btn btn-outline-primary btn-sm w-100"
                      >
                        <i className="fas fa-eye me-1" aria-hidden></i>
                        View Event Details
                      </Link>
                    )}
                    {app.status === 'draft' && event && (
                      <Link
                        to={`/events/${event.slug}/apply`}
                        className="btn btn-primary btn-sm w-100 mt-2"
                      >
                        <i className="fas fa-pen me-1" aria-hidden></i>
                        Continue Application
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyApplicationsPage;
